/**
 * Org State Service — Story 110.7
 * Управляет активной организацией пользователя.
 * Зеркалирует CabinetStateService (Story 21.9).
 * Использует Angular signals + localStorage для персистентности.
 */
import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RouteReloadService } from '../routing/route-reload.service';

export interface Org {
  company_id: string;
  name: string;
  role: string;
  is_owner: boolean;
}

const STORAGE_KEY = 'active_company_id';

@Injectable({ providedIn: 'root' })
export class OrgStateService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private routeReload = inject(RouteReloadService);

  private _orgs = signal<Org[]>([]);
  private _activeOrgId = signal<string>(
    localStorage.getItem(STORAGE_KEY) || ''
  );

  /** Список всех доступных организаций */
  readonly orgs = this._orgs.asReadonly();

  /** ID активной организации */
  readonly activeOrgId = this._activeOrgId.asReadonly();

  /** Активная организация (объект) */
  readonly activeOrg = computed(() =>
    this._orgs().find(o => o.company_id === this._activeOrgId()) ?? null
  );

  /**
   * Показывать ли переключатель (1+ организация).
   * Cabinet показывает при 2+ (owner всегда 1 cabinet), для org-owner
   * обычно 1+ орг → показываем при ≥1.
   */
  readonly showSwitcher = computed(() => this._orgs().length >= 1);

  /**
   * Загрузить список организаций с backend.
   * Восстанавливает выбор из localStorage или авто-выбирает если 1 орг.
   * При 0 орг (cabinet-admin без org-роли) — activeOrgId='' (ALL).
   */
  async loadMyOrgs(): Promise<void> {
    try {
      const list = await firstValueFrom(
        this.http.get<Org[]>(`${environment.apiUrl}/org/my-orgs`)
      );
      this._orgs.set(list);

      // Восстановить выбор из localStorage, проверяя актуальность
      const saved = localStorage.getItem(STORAGE_KEY);
      const validSaved = saved
        ? list.find(o => o.company_id === saved)
        : null;

      if (validSaved) {
        // Сохранённая орг актуальна — используем её
        this._activeOrgId.set(saved!);
      } else if (list.length === 1) {
        // Ровно одна орг — авто-выбор
        this._activeOrgId.set(list[0].company_id);
        localStorage.setItem(STORAGE_KEY, list[0].company_id);
      } else {
        // 0 орг или 2+ без сохранённого выбора — нет фокуса (ALL)
        this._activeOrgId.set('');
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.error('[OrgStateService] Failed to load orgs:', err);
      // При ошибке сбросить невалидную орг из localStorage
      localStorage.removeItem(STORAGE_KEY);
      this._activeOrgId.set('');
    }
  }

  /**
   * Переключить активную организацию.
   * Сохраняет выбор в localStorage и перезагружает данные через Angular Router
   * (тот же подход что CabinetStateService — без window.location.reload).
   */
  switchOrg(id: string): void {
    if (id === this._activeOrgId()) return;
    // id === '' = режим «Все организации» (без фокуса = ALL) → удаляем ключ,
    // чтобы при следующей загрузке не «восстанавливать» пустую строку.
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    this._activeOrgId.set(id);

    // Зеркало switchCabinet() (Story 99.28): без '/'-bounce, чтобы layout не мигал.
    // onSameUrlNavigation + SameUrlRouteReuseStrategy пересоздают текущий экран;
    // request() ограничивает пересоздание этой единственной навигацией.
    const currentUrl = this.router.url;
    this.routeReload.request();
    void this.router.navigateByUrl(currentUrl).finally(() => this.routeReload.clear());
  }

  /**
   * Очистить состояние при logout.
   */
  clear(): void {
    this._orgs.set([]);
    this._activeOrgId.set('');
    localStorage.removeItem(STORAGE_KEY);
  }
}
